import { useUserContext } from "src/lib/contexts/UserContext";
import IMovieReview from "src/lib/filmwebid/IMovieReview";
import useUserApi from "src/lib/filmwebid/useUserApi";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { buildUserApiQueryKey, UserApiQueryKey, useUserApiQuery, useUserApiQueryFunction } from "src/lib/filmwebid/useUserApiQuery";
import { InfiniteData, QueryFunctionContext, useInfiniteQuery } from "@tanstack/react-query";


export default function useFilmReviewList(edi: string | null | undefined, streamingId: number | null | undefined): [reviews: IMovieReview[], totalReviews: number, isWorking: boolean, fetchNext: (numToFetch: number) => void] {

	const authContext = useUserContext();
	const fetchWrittenReviews = useUserApiQueryFunction("GET", true, false);

	const numToFetch = useRef(INITIAL_COUNT);

	const commonParams = {
		edi: edi ?? undefined,
		streamingId: streamingId ?? undefined
	}
	const queryKey = buildUserApiQueryKey("/api/Movie/MovieReviews", {
		...commonParams,
	});

	const { data: writtenReviews, isLoading: isLoadingWrittenReviews, fetchNextPage } = useInfiniteQuery<IMovieReviewsResponse, unknown, InfiniteData<IMovieReviewsResponse>, UserApiQueryKey>({
		queryFn: fetchWrittenReviews,
		queryKey: queryKey,
		getNextPageParam: (lastPage, allPages) => {
			const startIndex = allPages.reduce((acc, page) => acc + (page.reviews?.length ?? 0), 0);
			if (startIndex < lastPage.totalReviews) {
				return {
					startIndex: startIndex,
					count: numToFetch.current
				};
			}
			return undefined;
		},
		initialPageParam: { startIndex: 0, count: numToFetch.current },
	})

	const { data: likedReviews, isLoading: isLoadingLikedReviews } = useUserApiQuery<string[]>("/api/Reviews/GetLiked", commonParams, {
		method: "GET",
		allowAnon: true
	},
		{
			enabled: authContext.isAuthenticated
		});
	const isLoading = isLoadingWrittenReviews || isLoadingLikedReviews;

	const { reviews, totalReviews } = useMemo(() => {
		let totalReviews = 0;
		let reviews: IMovieReview[] = writtenReviews?.pages?.flatMap((page) => page.reviews ?? []).map((review) => {
			if (likedReviews?.includes(review.userId)) {
				review.isLiked = true;
			}
			return review;
		}) ?? []
		if (writtenReviews?.pages) {
			totalReviews = writtenReviews.pages[0].totalReviews;
		}
		return { reviews, totalReviews };
	}, [writtenReviews, likedReviews]);

	const fetchNext = useCallback((count: number) => {
		numToFetch.current = count;
		fetchNextPage();
	}, [fetchNextPage]);

	return [reviews, totalReviews, isLoading, fetchNext];

}


const INITIAL_COUNT = 6;

interface IMovieReviewsResponse {
	totalReviews: number;
	reviews: IMovieReview[];
}
